import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import TagAlongDialog, { daysLeft } from "@/components/TagAlongDialog";

function embedUrl(url) {
  if (!url) return null;
  const clean = url.trim();
  // If LinkedIn embed already
  if (clean.includes("linkedin.com/embed")) return clean;
  // LinkedIn post URL
  const m = clean.match(/urn:li:(?:activity|ugcPost|share):(\d+)/);
  if (m) return `https://www.linkedin.com/embed/feed/update/urn:li:activity:${m[1]}`;
  // YouTube support
  const yt = clean.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return clean;
}

const OUTCOME_LABEL = {
  invested: { label: "Invested €20,000", cls: "bg-success text-black" },
  passed: { label: "Passed", cls: "bg-danger text-white" },
  follow_up: { label: "Follow-up", cls: "bg-warn text-black" },
};

export default function EpisodeDetail() {
  const { slug } = useParams();
  const [ep, setEp] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/episodes/${slug}`)
      .then(({ data }) => setEp(data))
      .catch((e) => setErr(e.response?.status === 404 ? "Episode not found" : "Failed to load"));
  }, [slug]);

  const share = (platform) => {
    const url = window.location.href;
    const text = `${ep?.startup_name} — ${ep?.one_liner}`;
    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      toast.success("Link copied");
      return;
    }
    const shareUrls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    };
    window.open(shareUrls[platform], "_blank");
  };

  if (err) return (
    <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
      <div className="font-heading text-4xl font-black uppercase">{err}</div>
      <Link to="/episodes" className="text-lime mt-4 inline-block">← Back to episodes</Link>
    </div>
  );
  if (!ep) return <div className="text-white/50 font-mono text-sm uppercase tracking-widest py-32 text-center">Loading…</div>;

  const oc = OUTCOME_LABEL[ep.outcome] || { label: ep.outcome, cls: "bg-white/10 text-white" };
  const video = embedUrl(ep.linkedin_video_url);

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12" data-testid="episode-detail">
      <Link to="/episodes" className="text-xs font-mono uppercase tracking-widest text-white/50 hover:text-lime">← Library</Link>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold ${oc.cls}`}>{oc.label}</span>
        {ep.industry && <span className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-white/20 text-white/70">{ep.industry}</span>}
        {ep.country && <span className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-white/20 text-white/70">{ep.country}</span>}
      </div>

      <h1 data-testid="episode-title" className="mt-6 font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.95]">{ep.startup_name}</h1>
      <p className="mt-6 text-xl md:text-2xl text-white/70 max-w-3xl leading-snug">{ep.one_liner}</p>

      {/* Video */}
      <div className="mt-12 aspect-video bg-surface border border-white/10 relative overflow-hidden">
        {video ? (
          <iframe
            data-testid="episode-video"
            src={video}
            className="w-full h-full"
            allow="autoplay; encrypted-media"
            allowFullScreen
            title={ep.startup_name}
          />
        ) : ep.cover_image_path ? (
          <img src={fileUrl(ep.cover_image_path)} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 font-mono uppercase tracking-widest text-sm">Video not yet published</div>
        )}
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2 space-y-10">
          {ep.long_description && (
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-4">The Pitch</div>
              <p className="text-white/80 text-lg leading-relaxed whitespace-pre-wrap">{ep.long_description}</p>
            </div>
          )}
          {ep.host_notes && (
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-4">Host's Takeaways</div>
              <p className="text-white/80 text-lg leading-relaxed whitespace-pre-wrap">{ep.host_notes}</p>
            </div>
          )}
        </div>
        <aside className="space-y-6">
          {ep.outcome === "invested" && ep.tag_along_enabled !== false && (
            <div className="bg-lime text-black p-6" data-testid="tag-along-cta">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-3">Tag along</div>
              <div className="font-heading text-2xl font-black uppercase tracking-tight leading-tight mb-3">Invest alongside on the same terms.</div>
              <p className="text-black/70 text-sm mb-4 leading-relaxed">
                Same valuation, same paperwork. Minimum ticket €5,000 per investor. 30-day window from episode publication.
              </p>
              {ep.tag_along_total > 0 && (
                <div className="text-xs font-mono uppercase tracking-widest mb-2 text-black/70">
                  So far tagged along: <span className="font-bold text-black">€{Number(ep.tag_along_total).toLocaleString()}</span>
                </div>
              )}
              {daysLeft(ep.episode_date) > 0 ? (
                <>
                  <div data-testid="ta-timer" className="text-xs font-mono uppercase tracking-widest mb-4 text-black/70">
                    Closes in <span className="font-bold text-black tabular-nums">{daysLeft(ep.episode_date)}</span> {daysLeft(ep.episode_date) === 1 ? "day" : "days"}
                  </div>
                  <TagAlongDialog episode={ep} />
                </>
              ) : (
                <div data-testid="ta-closed" className="mt-2 py-3 bg-black text-white font-heading font-black uppercase tracking-wider text-center">
                  Opportunity closed
                </div>
              )}
            </div>
          )}
          {ep.outcome !== "invested" && ep.solo_invest_enabled === true && (
            <div className="bg-surface border-2 border-lime text-white p-6" data-testid="solo-invest-cta">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-3 text-lime">Invest anyway</div>
              <div className="font-heading text-2xl font-black uppercase tracking-tight leading-tight mb-3">Simone passed. You don't have to.</div>
              <p className="text-white/70 text-sm mb-4 leading-relaxed">
                The host didn't write the €20K check on this one — but if you believe in {ep.startup_name}, you can still express interest. Terms are negotiated directly with the founder. Minimum ticket €5,000.
              </p>
              <TagAlongDialog
                episode={ep}
                mode="solo_invest"
                triggerTestId="solo-invest-open-btn"
                trigger={(open) => (
                  <button
                    data-testid="solo-invest-open-btn"
                    onClick={open}
                    className="w-full py-3 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.02] transition-transform"
                  >
                    I wish to invest anyway →
                  </button>
                )}
              />
            </div>
          )}
          <div className="bg-surface border border-white/10 p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">Startup</div>
            <dl className="space-y-4 text-sm">
              {ep.website && (
                <div>
                  <dt className="text-white/40 font-mono text-[10px] uppercase">Website</dt>
                  <dd><a href={ep.website.startsWith("http") ? ep.website : `https://${ep.website}`} target="_blank" rel="noreferrer" className="text-lime hover:underline break-all">{ep.website}</a></dd>
                </div>
              )}
              {ep.country && (<div><dt className="text-white/40 font-mono text-[10px] uppercase">Country</dt><dd className="text-white">{ep.country}</dd></div>)}
              {ep.year_incorporation && (<div><dt className="text-white/40 font-mono text-[10px] uppercase">Founded</dt><dd className="text-white">{ep.year_incorporation}</dd></div>)}
              {ep.industry && (<div><dt className="text-white/40 font-mono text-[10px] uppercase">Industry</dt><dd className="text-white">{ep.industry}</dd></div>)}
              {ep.view_count != null && (<div><dt className="text-white/40 font-mono text-[10px] uppercase">Views</dt><dd className="text-white tabular-nums">{ep.view_count}</dd></div>)}
            </dl>
          </div>
          <div className="bg-surface border border-white/10 p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">Share</div>
            <div className="flex flex-col gap-2">
              <button data-testid="share-linkedin" onClick={() => share("linkedin")} className="px-4 py-3 border border-white/10 text-left hover:border-lime hover:text-lime font-mono text-xs uppercase tracking-widest transition-colors">Share on LinkedIn →</button>
              <button data-testid="share-x" onClick={() => share("x")} className="px-4 py-3 border border-white/10 text-left hover:border-lime hover:text-lime font-mono text-xs uppercase tracking-widest transition-colors">Share on X →</button>
              <button data-testid="share-copy" onClick={() => share("copy")} className="px-4 py-3 border border-white/10 text-left hover:border-lime hover:text-lime font-mono text-xs uppercase tracking-widest transition-colors">Copy link</button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

